<template>
  <div>
    <el-dialog
        id="accountVerification-verificationMethod"
        :visible.sync="dialogVisibility"
        width="650px"
        :show-close="true"
        title="Account Verification"
        class="verification-method-modal"
        :before-close="processCloseRequest"
        @open="initialize">
      <error-legend
          v-if="errorMessage"
          :message="errorMessage" />

      <div class="steps-container">
        <!-- Shared steps -->
        <VerificationMethodSelectionStep
            v-if="currentVerificationStep === VERIFICATION_STEPS.METHOD_SELECTION"
            @methodSelected="setVerificationMethod"
            @nextStepRequested="setNextStep"
            @previousStepRequested="setPreviousStep"
            @verificationChannelsUpdated="updateAvailableVerificationChannels" />

        <DataConfirmationStep
            v-else-if="currentVerificationStep === VERIFICATION_STEPS.DATA_CONFIRMATION"
            :verification-method-type="currentVerificationMethod"
            :is-resend-code-request="isResendConfirmationCode"
            :icon="activeIcon"
            :allowed-verification-methods="allowedVerificationMethods"
            @nextStepRequested="setNextStep"
            @previousStepRequested="setPreviousStep"
            @connectionError="showConnectionErrorStep" />

        <!-- Ring specific steps -->
        <PasscodeConfirmationStep
            v-else-if="currentVerificationStep === VERIFICATION_STEPS.PASSCODE_CONFIRMATION"
            ref="passcodeConfirmationStepRef"
            :verification-method-type="currentVerificationMethod"
            :icon="activeIcon"
            @nextStepRequested="setNextStep"
            @previousStepRequested="setPreviousStep"
            @errorNotification="processErrorNotification"
            @complete="completeAccountVerification"
            @connectionError="showConnectionErrorStep"
            @tryDifferentMethod="setTryDifferentMethodStep" />

        <!-- Ring App Control Center -->
        <RingAppControlCenterInstructions
            v-else-if="currentVerificationStep === VERIFICATION_STEPS.RING_APP_VERIFICATIONS_INSTRUCTIONS"
            :verification-method-type="currentVerificationMethod"
            :icon="activeIcon"
            @nextStepRequested="setNextStep"
            @previousStepRequested="setPreviousStep"
            @errorNotification="processErrorNotification"
            @complete="completeAccountVerification" />

        <!-- Override Authorization -->
        <VerifyOverride
            v-else-if="currentVerificationStep === VERIFICATION_STEPS.OVERRIDE_AUTHORIZATION_STEP"
            @cancelOverrideRequested="cancelOverride"
            @connectionError="showConnectionErrorStep" />

        <!-- General exit and error handling -->
        <InternetConnectionErrorStep
            v-if="currentVerificationStep === VERIFICATION_STEPS.CONNECTION_ERROR_STEP"
            @previousStepRequested="setPreviousStep" />

        <!--        <exit-confirmation-->
        <!--            v-if="currentVerificationStep === VERIFICATION_STEPS.EXIT_CONFIRMATION_STEP"-->
        <!--            @exitResponse="processExitResponse" />-->
      </div>
    </el-dialog>
  </div>
</template>

<script>
import VerificationMethodSelectionStep from "./VerificationMethodSelectionStep";
import DataConfirmationStep from "./DataConfirmationStep";
import PasscodeConfirmationStep from "../ring/PasscodeConfirmationStep";
import ErrorLegend from "./ErrorLegend";
import InternetConnectionErrorStep from "./InternetConnectionErrorStep";
import ExitConfirmation from "./ExitConfirmation";
import { createNamespacedHelpers as module } from "vuex";
import VerifyOverride from "../override/VerifyOverride";
import RingAppControlCenterInstructions from "../ring/RingAppControlCenterInstructions";
const { mapGetters: accountGetters, mapActions: accountActions } =
  module("db/account");

const {
  mapGetters: accountVerificationGetters,
  mapActions: accountVerificationActions,
} = module("db/account/verification");

const VERIFICATION_METHODS_MAP = {
  EMAIL: "email",
  SMS: "sms",
  RING_APP_CONTROL_CENTER: "ring_app_cc",
};

const VERIFICATION_METHODS = {
  EMAIL: [VERIFICATION_METHODS_MAP.EMAIL],
  SMS: [VERIFICATION_METHODS_MAP.SMS],
};

const ICONS = {
  email: require("@/assets/icons/new-ui/customer/authorizations/shared/mail.svg"),
  sms: require("@/assets/icons/new-ui/customer/authorizations/shared/sms.svg"),
  EmailLink: require("@/assets/icons/new-ui/customer/authorizations/shared/mail.svg"),
  ring_app_cc: require("@/assets/icons/new-ui/customer/authorizations/shared/app_push.svg"),
  PhoneWeb: require("@/assets/icons/new-ui/customer/authorizations/shared/sms.svg"),
};

export default {
  name: "VerificationMethodModal",
  components: {
    VerifyOverride,
    ExitConfirmation,
    InternetConnectionErrorStep,
    ErrorLegend,
    VerificationMethodSelectionStep,
    DataConfirmationStep,
    PasscodeConfirmationStep,
    RingAppControlCenterInstructions,
  },
  props: {
    isDialogVisible: {
      type: Boolean,
      required: true,
      default: () => {
        return false;
      },
    },
    isVerificationOverride: {
      type: Boolean,
      required: true,
      default: false,
    },
    // eslint-disable-next-line vue/require-default-prop
    onCloseAction: {
      type: Function,
      required: false,
    },
  },
  provide: {
    VERIFICATION_METHODS,
    VERIFICATION_METHODS_MAP,
    ICONS,
  },
  emits: ["visibilityChanged"],
  data() {
    return {
      currentVerificationMethod: null,
      currentVerificationStep: null,
      isResendConfirmationCode: false,
      errorMessage: null,
      confirmCloseFunction: null,
      beforeCloseState: null,
      previousStep: null,
      activeIcon: null,
      VERIFICATION_STEPS: Object.freeze({
        METHOD_SELECTION: "method-selection-step",
        DATA_CONFIRMATION: "data-confirmation-step",
        PASSCODE_CONFIRMATION: "passcode-confirmation-step",
        CONNECTION_ERROR_STEP: "connection-error-step",
        EXIT_CONFIRMATION_STEP: "exit-confirmation-step",
        OVERRIDE_AUTHORIZATION_STEP: "override-authorization-step",
        RING_APP_VERIFICATIONS_INSTRUCTIONS: "ring-control-center-verification",
      }),
      allowedVerificationMethods: null,
    };
  },
  computed: {
    ...accountGetters(["verificationChannels", "isFirstVerificationAttempt"]),
    ...accountVerificationGetters([
      "getCurrentVerificationStep",
      "getCurrentVerificationMethod",
      "getActiveIcon",
      "getAllowedVerificationMethods",
      "getBackendAuthorizationStatus",
    ]),
    dialogVisibility: {
      get() {
        return this.isDialogVisible;
      },
      set(visible) {
        this.$emit("visibilityChanged", visible);
      },
    },
    verificationMethodsContainsSms() {
      return (
        this.verificationChannels.filter((channel) =>
          VERIFICATION_METHODS.SMS.includes(channel.value)
        ).length > 0
      );
    },
    restorableStates() {
      return [
        this.VERIFICATION_STEPS.RING_APP_VERIFICATIONS_INSTRUCTIONS,
        this.VERIFICATION_STEPS.PASSCODE_CONFIRMATION,
      ];
    },
  },
  watch: {
    currentVerificationStep(newStep, oldStep) {
      const isRestoredWindow = oldStep === null;
      if (isRestoredWindow) {
        return;
      }

      if (this.restorableStates.includes(newStep)) {
        this.saveState(newStep);
      }
    },
  },
  methods: {
    ...accountActions(["setIsFirstAuthorizationAttempt"]),
    ...accountVerificationActions(["setCurrentAccountVerificationState"]),
    restoreState() {
      const currentVerificationStep = this.getCurrentVerificationStep;
      if (this.restorableStates.includes(currentVerificationStep)) {
        this.currentVerificationStep = currentVerificationStep;
        this.currentVerificationMethod = this.getCurrentVerificationMethod;
        this.activeIcon = this.getActiveIcon;
        this.allowedVerificationMethods = this.getAllowedVerificationMethods;
        return true;
      }

      return false;
    },
    saveState(newStep) {
      this.setCurrentAccountVerificationState({
        currentVerificationStep: newStep,
        currentVerificationMethod: this.currentVerificationMethod,
        activeIcon: this.activeIcon,
        allowedVerificationMethods: this.allowedVerificationMethods,
        startedAt: new Date().getTime(),
        backendAuthorizationStatus: "pending",
      });
    },
    setVerificationMethod(selectedMethod) {
      this.currentVerificationMethod = selectedMethod;
      this.activeIcon = ICONS[selectedMethod];
    },
    setNextStep() {
      this.errorMessage = null;
      this.setNextStepRing();
    },
    setNextStepRing() {
      this.previousStep = this.currentVerificationStep;
      if (
        this.currentVerificationMethod ===
        VERIFICATION_METHODS_MAP.RING_APP_CONTROL_CENTER
      ) {
        this.setNextStepForRingAppControlCenter();
      } else {
        this.setNextStepForRingEmailOrSms();
      }
    },
    setNextStepForRingAppControlCenter() {
      switch (this.currentVerificationStep) {
        case this.VERIFICATION_STEPS.METHOD_SELECTION:
          this.currentVerificationStep =
            this.VERIFICATION_STEPS.RING_APP_VERIFICATIONS_INSTRUCTIONS;
          break;
        case this.VERIFICATION_STEPS.RING_APP_VERIFICATIONS_INSTRUCTIONS:
          this.currentVerificationStep =
            this.VERIFICATION_STEPS.PASSCODE_CONFIRMATION;
          break;
        default:
      }
    },
    setNextStepForRingEmailOrSms() {
      switch (this.currentVerificationStep) {
        case this.VERIFICATION_STEPS.METHOD_SELECTION:
          this.currentVerificationStep =
            this.VERIFICATION_STEPS.DATA_CONFIRMATION;
          break;
        case this.VERIFICATION_STEPS.DATA_CONFIRMATION:
          this.currentVerificationStep =
            this.VERIFICATION_STEPS.PASSCODE_CONFIRMATION;
          break;
        default:
      }
    },
    setPreviousStep() {
      this.errorMessage = null;

      if (this.isVerificationOverride) {
        this.currentVerificationStep =
          this.VERIFICATION_STEPS.OVERRIDE_AUTHORIZATION_STEP;
      } else {
        this.setPreviousStepRing();
      }
    },
    setTryDifferentMethodStep() {
      this.errorMessage = null;
      this.isResendConfirmationCode = false;
      this.currentVerificationStep = this.VERIFICATION_STEPS.METHOD_SELECTION;
    },
    setPreviousStepRing() {
      switch (this.currentVerificationStep) {
        case this.VERIFICATION_STEPS.DATA_CONFIRMATION:
          this.currentVerificationStep =
            this.VERIFICATION_STEPS.METHOD_SELECTION;
          this.isResendConfirmationCode = false;
          break;
        case this.VERIFICATION_STEPS.PASSCODE_CONFIRMATION:
        case this.VERIFICATION_STEPS.CONNECTION_ERROR_STEP:
          this.currentVerificationStep = this.previousStep;

          if (
            this.currentVerificationStep ===
            this.VERIFICATION_STEPS.DATA_CONFIRMATION
          ) {
            this.isResendConfirmationCode = true;
          }
          break;
        case this.VERIFICATION_STEPS.RING_APP_VERIFICATIONS_INSTRUCTIONS:
          this.currentVerificationStep =
            this.VERIFICATION_STEPS.METHOD_SELECTION;
          this.isResendConfirmationCode = false;
          break;
      }
    },
    initialize() {
      this.errorMessage = null;
      this.isResendConfirmationCode = false;

      const isTimedOut = this.getBackendAuthorizationStatus === "TimedOut";
      const isRevoked = this.getBackendAuthorizationStatus === "revoked";

      // Restoring minimize state if present
      if (
        !isTimedOut &&
        !isRevoked &&
        !this.isVerificationOverride &&
        this.restoreState()
      ) {
        this.setFocusForPasscodeInput();
        return;
      }

      // Skipping to SMS if available
      if (
        this.isFirstVerificationAttempt &&
        this.verificationMethodsContainsSms &&
        !this.isVerificationOverride
      ) {
        this.moveToSmsStep();
        return;
      }

      // Skipping method selection if there is a single verification method available
      if (
        !this.isVerificationOverride &&
        this.verificationChannels.length === 1
      ) {
        this.skipVerificationMethodSelection();
        return;
      }

      // Normal flow
      if (this.isVerificationOverride) {
        this.currentVerificationStep =
          this.VERIFICATION_STEPS.OVERRIDE_AUTHORIZATION_STEP;
      } else {
        this.currentVerificationStep = this.VERIFICATION_STEPS.METHOD_SELECTION;
      }
    },
    moveToSmsStep() {
      this.setVerificationMethod(VERIFICATION_METHODS_MAP.SMS);
      this.updateAvailableVerificationChannels(this.verificationChannels);

      this.currentVerificationStep = this.VERIFICATION_STEPS.DATA_CONFIRMATION;
      this.setIsFirstAuthorizationAttempt(false);
    },
    skipVerificationMethodSelection() {
      this.currentVerificationStep = this.VERIFICATION_STEPS.METHOD_SELECTION;
      let verificationChannel = this.verificationChannels[0].value;
      this.updateAvailableVerificationChannels(this.verificationChannels);
      this.setVerificationMethod(verificationChannel);
      this.setNextStep();
    },
    processErrorNotification(newErrorValue) {
      this.errorMessage = newErrorValue;
    },
    completeAccountVerification(success) {
      if (success) {
        this.setIsFirstAuthorizationAttempt(true);
        this.$emit("visibilityChanged", false);
        // Refreshing the ui to reload information
        this.$router.go(this.$router.currentRoute);
      }
    },
    showConnectionErrorStep(value) {
      this.previousStep = this.currentVerificationStep;

      this.currentVerificationStep =
        this.VERIFICATION_STEPS.CONNECTION_ERROR_STEP;
    },
    processCloseRequest(done) {
      this.errorMessage = null;
      this.onCloseAction;
      done();
    },
    cancelOverride() {
      this.$emit("visibilityChanged", false);
    },
    updateAvailableVerificationChannels(updatedVerificationChannels) {
      this.allowedVerificationMethods = updatedVerificationChannels;
    },
    setFocusForPasscodeInput() {
      // Next Tick lets vue to wait until child components are rendered and then applies the request.
      this.$nextTick(() => {
        this.$refs["passcodeConfirmationStepRef"].$refs["passcodeReader"].$refs[
          "passcodeTxt"
        ].focus();
      });
    },
  },
};
</script>


<style scoped></style>

<style>
.verification-method-modal div.el-dialog__header > span.el-dialog__title {
  width: 554px;
  height: 24px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  color: #424242;

  display: table;
  margin: 0 auto;
}

.verification-method-modal div.el-dialog__header {
  border-bottom: 1px solid #dee5ec;
}

.verification-method-modal .el-dialog__body {
  padding: 0;
}

.steps-container {
  padding: 28px 40px 40px 40px;
}
</style>
